import RequestFeatureDialog from "@/components/RequestFeatureDialog";
import { myFeaturesQueryOptions } from "@/queries/feedback/myFeaturesQuery";
import { useQuery } from "@tanstack/react-query";
import {
  requestedFeaturesQueryOptions,
  useDownvoteFeatureState,
  useRequestedFeatures,
  useUpvoteFeatureState,
} from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import { RequestedFeature } from "@vixi/types";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FeatureDetailsDialog,
  FeatureListItem,
  FeatureListList,
  FeatureListRoot,
  FeatureListTitle,
  FormDescription,
  FormHeading,
  LoadingSpinner,
  PageMainContent,
  TextLink,
  useGoBack,
} from "@vixi/ui";
import { useEffect, useState } from "react";

const routeId = "/_app/feedback/" satisfies RouterIds;
const Route = router.routesById[routeId];

export const FeedbackIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient }, deps: { search } }) => {
    const { featureId } = search;
    if (featureId) {
      queryClient.ensureQueryData(requestedFeaturesQueryOptions());
    }
  },
};

function RouteComponent() {
  useGoBack(null);
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { featureId, me } = Route.useSearch();

  const { data: requestedFeatures, isPending, dataUpdatedAt } = useRequestedFeatures();
  const { data: myFeatures } = useQuery({
    ...myFeaturesQueryOptions({
      pageNumber: 1,
      pageSize: 1000,
    }),
    enabled: me,
  });

  // Feature details dialog
  const [isFeatureDetailsOpen, setFeatureDetailsOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<RequestedFeature | null>(null);

  // TODO: Replace this to use featureByIdQueryOptions
  useEffect(() => {
    if (featureId) {
      const feature = requestedFeatures?.items?.find((f) => f.id === featureId) ?? null;
      setSelectedFeature(feature);
      if (feature) setFeatureDetailsOpen(true);
    } else {
      setFeatureDetailsOpen(false);
    }
  }, [featureId, dataUpdatedAt]);

  // Request feature dialog
  const [isRequestFeatureOpen, setRequestFeatureOpen] = useState(false);

  // Subscribe to upvotes and downvotes
  const _upvoteMutations = useUpvoteFeatureState();
  const _downvoteMutations = useDownvoteFeatureState();

  return (
    <PageMainContent scrollable ps="medium">
      <div className="bg-white   rounded flex items-center justify-between">
      <FormHeading heading="Feedback.Title">
        <FormDescription>{t("Feedback.Subtitle")}</FormDescription>
      </FormHeading>
        <img src="/images/feadback.webp" alt="Feedback" className="w-[95px] h-[84px]  me-7.5" />
      </div>
      <div className="flex items-center gap-4">
        <div className="bg-paper mt-2.5 flex max-w-162.5 items-center gap-5.25 rounded py-3.5 ps-10 pe-8.25">
          <p>{t("Feedback.RequestFeaturePrompt")}</p>
          <Button
            variant="outlined"
            size="small"
            className="text-md"
            onClick={() => setRequestFeatureOpen(true)}
          >
            {t("Action.RequestFeature")}
          </Button>
        </div>

        <TextLink
          underline="always"
          to={Route.fullPath}
          search={{
            me: true,
          }}
        >
          {t("Feedback.ViewYourRequests")}
        </TextLink>
      </div>

      <FeatureListRoot className="mt-5">
        <FeatureListTitle>{t("Feedback.UpvoteFeatures")}</FeatureListTitle>

        <FeatureListList>
          {isPending ? (
            <div className="m-auto w-fit">
              <LoadingSpinner />
            </div>
          ) : (
            requestedFeatures?.items?.map((feature) => (
              <FeatureListItem key={feature.id} feature={feature} />
            ))
          )}
        </FeatureListList>
      </FeatureListRoot>

      <Dialog
        open={me ?? false}
        onOpenChange={(open) => {
          if (!open) {
            navigate({
              search: (prev) => ({
                ...prev,
                me: false,
              }),
            });
          }
        }}
      >
        <DialogContent className="max-w-8/12">
          <div className="flex justify-between">
            <DialogTitle>{t("Feedback.YourRequests")}</DialogTitle>
          </div>

          <FeatureListList className="max-h-[60vh]">
            {isPending ? (
              <div className="m-auto w-fit py-4">
                <LoadingSpinner />
              </div>
            ) : (
              myFeatures?.items?.map((feature) => (
                <FeatureListItem key={feature.id} feature={feature} />
              ))
            )}
          </FeatureListList>
        </DialogContent>
      </Dialog>

      <FeatureDetailsDialog
        open={isFeatureDetailsOpen}
        onOpenChange={(open) => {
          if (!open)
            navigate({
              search: {},
              replace: true,
            });
        }}
        feature={selectedFeature}
      />
      <RequestFeatureDialog
        open={isRequestFeatureOpen}
        onClose={() => setRequestFeatureOpen(false)}
        onSubmit={() => {}}
      />
    </PageMainContent>
  );
}
